import { useEffect, useRef, useCallback } from 'react'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { X } from 'lucide-react'
import { useStoryWriterStore } from '../store/storyWriterStore'
import { useGenerateStories } from '../hooks/useGenerateStories'

const DRAFT_KEY = 'story_writer_draft'

export function StoryWriterForm() {
  const { inputText, status, setInputText, reset } = useStoryWriterStore()
  const { generate, abort } = useGenerateStories()

  const isStreaming = status === 'streaming'
  const submitRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Draft persistence
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY)
    if (saved) setInputText(saved)
  }, [setInputText])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (inputText) {
        localStorage.setItem(DRAFT_KEY, inputText)
      } else {
        localStorage.removeItem(DRAFT_KEY)
      }
    }, 500)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [inputText])

  const handleSubmit = useCallback(() => {
    if (submitRef.current) clearTimeout(submitRef.current)
    submitRef.current = setTimeout(() => void generate(), 300)
  }, [generate])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      if (isStreaming) {
        abort()
      } else {
        reset()
        localStorage.removeItem(DRAFT_KEY)
      }
    }
  }

  useEffect(() => {
    return () => {
      if (submitRef.current) clearTimeout(submitRef.current)
    }
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="draft-notes">Draft notes</Label>
        <Textarea
          id="draft-notes"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Paste your draft notes here..."
          className="min-h-[200px] resize-y font-mono text-sm"
          disabled={isStreaming}
        />
        <p className="text-xs text-muted-foreground">
          {inputText.length} characters &middot; Cmd+Enter to generate &middot; Esc to {isStreaming ? 'abort' : 'clear'}
        </p>
      </div>

      <div className="flex gap-2">
        {isStreaming ? (
          <Button variant="outline" onClick={abort} className="gap-2">
            <X className="h-4 w-4" />
            Abort
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!inputText.trim()}
            className="gap-2"
          >
            Generate Stories
          </Button>
        )}
        {!isStreaming && (inputText || status !== 'idle') && (
          <Button variant="ghost" onClick={() => { reset(); localStorage.removeItem(DRAFT_KEY) }}>
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}
